import { motion } from "framer-motion";
import { SKILLS } from "@/constants/testIds/ashwin";
import { skills, education, certifications } from "@/data/resume";

export default function Skills() {
  const groups = Object.entries(skills);
  return (
    <section
      id="skills"
      data-testid={SKILLS.root}
      className="py-24 md:py-40 border-t border-[color:var(--border)] bg-[color:var(--linen-2)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-y-8 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-6">
            <div className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em] mb-6">
              04 — Craft
            </div>
            <h2 className="font-display text-5xl md:text-6xl font-light tracking-tight leading-[0.95]">
              What I <span className="font-serif-accent italic text-[color:var(--accent)]">actually</span> do.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 text-lg leading-relaxed text-[color:var(--charcoal)]/80 self-end">
            A cross-functional toolkit — strategy, brand, data, and the plumbing that
            connects them.
          </div>
        </div>

        {/* Skills bento */}
        <div className="grid md:grid-cols-3 grid-divider border border-[color:var(--border)] bg-[color:var(--linen)]">
          {groups.map(([group, items], gi) => (
            <motion.div
              key={group}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: gi * 0.1 }}
              className="p-8 md:p-10"
            >
              <div className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em] mb-6">
                0{gi + 1}
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight leading-tight mb-6">
                {group}
              </h3>
              <ul className="space-y-2">
                {items.map((s) => (
                  <li
                    key={s}
                    className="text-base border-b border-[color:var(--border)] py-2 hover:text-[color:var(--accent)] transition-colors cursor-default flex items-center justify-between"
                  >
                    <span>{s}</span>
                    <span className="text-[color:var(--muted)] font-mono-editorial text-xs">
                      ✦
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Education + Certifications */}
        <div className="mt-20 md:mt-28 grid md:grid-cols-2 gap-12 md:gap-24">
          <div>
            <div className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em] mb-6">
              Education
            </div>
            <div className="space-y-6">
              {education.map((e) => (
                <div key={e.degree} className="border-b border-[color:var(--border)] pb-6">
                  <div className="font-display text-2xl font-light leading-tight">
                    {e.degree}
                  </div>
                  <div className="mt-1 font-serif-accent italic text-[color:var(--charcoal)]/75">
                    {e.school}
                  </div>
                  <div className="mt-2 text-sm text-[color:var(--muted)] font-mono-editorial flex justify-between">
                    <span>{e.years}</span>
                    <span>{e.grade}</span>
                  </div>
                  {e.activities?.length > 0 && (
                    <ul className="mt-4 space-y-2">
                      {e.activities.map((a, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-3 text-sm leading-relaxed text-[color:var(--charcoal)]/85"
                        >
                          <span className="mt-1.5 w-1 h-1 bg-[color:var(--accent)] shrink-0" />
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em] mb-6">
              Certifications
            </div>
            <ul className="space-y-3">
              {certifications.map((c) => (
                <li
                  key={c}
                  className="flex items-start gap-3 leading-relaxed"
                >
                  <span className="mt-2 w-1.5 h-1.5 bg-[color:var(--charcoal)]" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
